import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Firebase Imports
import { getAuth } from "firebase/auth"; 
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase"; 

// Standardization & Dictionary Utility
import {
  STANDARD_CUISINES,
  STANDARD_MEASUREMENTS,
  SEED_INGREDIENTS,
  toTitleCase,
  getStandardizedUnit,
  parseFraction,
  parseIngredientLine,
  parseBulkIngredients,
  findDuplicateIngredients
} from "../utils/recipeStandardization";

// Dnd-kit imports
import { DndContext, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// MUI Components & Icons
import {
  Container, Paper, Typography, Grid, TextField, Button, Box, Select, MenuItem, InputLabel,
  FormControl, IconButton, CircularProgress, Alert, Autocomplete, Chip, Divider, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkIcon from '@mui/icons-material/Link';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AiRecipeAssistantDialog from '../components/AiRecipeAssistantDialog';

const SCRAPE_URL = "https://us-central1-recipe-book-app-1.cloudfunctions.net/scrape_recipe";

const SortableStep = ({ step, index, handleStepChange, deleteStep, error }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
      <Box sx={{ pt: 1.5, cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIndicatorIcon color="action" />
      </Box>
      <Typography sx={{ pt: 2, minWidth: 24, fontWeight: 'bold' }}>{index + 1}.</Typography>
      <TextField
        label={`Step ${index + 1}`}
        value={step.instruction}
        onChange={(e) => handleStepChange(index, e.target.value)}
        fullWidth 
        multiline 
        error={!!error} 
        helperText={error}
        placeholder="e.g. Preheat oven to 375°F and line baking sheets with parchment paper."
      />
      <IconButton onClick={() => deleteStep(index)} color="error" sx={{ mt: 1 }}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const CreateRecipe = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const fileInputRef = useRef(null);

  // STATE
  const [recipeData, setRecipeData] = useState({
    recipe_name: "",
    description: "",
    cuisine: "American",
    prep_time: "",
    cook_time: "",
    servings: "4",
    difficulty: "Medium",
    ingredients: [{ ingredient_name: "", amount: "", measurement_name: "" }],
    steps: [{ id: Date.now(), step_number: 1, instruction: "" }],
    images: [],
    tags: []
  });

  // Central Dictionary State
  const [cuisinesList, setCuisinesList] = useState(STANDARD_CUISINES);
  const [ingredientsList, setIngredientsList] = useState(SEED_INGREDIENTS);

  // Quick Paste Dialog State
  const [quickPasteOpen, setQuickPasteOpen] = useState(false);
  const [quickPasteText, setQuickPasteText] = useState("");
  const [quickPasteMode, setQuickPasteMode] = useState("replace"); // 'replace' or 'append'

  // Image upload state
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState("");

  // Import state
  const [importUrl, setImportUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);

  // AI Recipe Assistant State
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  // Form Draft Recovery State
  const [draftAvailable, setDraftAvailable] = useState(null);

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const existingTags = [
    "Dinner", "Lunch", "Breakfast", "Dessert", "Vegetarian", "Vegan", 
    "Quick", "Healthy", "Comfort Food", "Holiday", "Slow Cooker", "Baking"
  ];

  // --- 1. FETCH CENTRAL DICTIONARY FROM FIRESTORE ---
  useEffect(() => {
    const fetchDictionary = async () => {
      try {
        const dictDoc = await getDoc(doc(db, "metadata", "dictionary"));
        if (dictDoc.exists()) {
          const data = dictDoc.data();
          if (data.cuisines && Array.isArray(data.cuisines) && data.cuisines.length > 0) {
            setCuisinesList(data.cuisines);
          }
          if (data.ingredients && Array.isArray(data.ingredients) && data.ingredients.length > 0) {
            setIngredientsList(data.ingredients);
          }
        }
      } catch (err) {
        console.log("Using local default dictionary fallback");
      }
    };
    fetchDictionary();
  }, []);

  // Check for unsaved draft in localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("astrup_create_recipe_draft");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.data && (parsed.data.recipe_name || (parsed.data.ingredients && parsed.data.ingredients[0]?.ingredient_name))) {
          setDraftAvailable(parsed);
        }
      }
    } catch (e) {
      console.warn("Could not read draft:", e);
    }
  }, []);

  // Auto-save form draft to localStorage (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      const hasContent = recipeData.recipe_name || 
        (recipeData.ingredients && recipeData.ingredients.some(i => i.ingredient_name)) ||
        (recipeData.steps && recipeData.steps.some(s => s.instruction));

      if (hasContent) {
        try {
          localStorage.setItem("astrup_create_recipe_draft", JSON.stringify({
            data: recipeData,
            savedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }));
        } catch (e) {
          // ignore quota error
        }
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [recipeData]);

  const handleRestoreDraft = () => {
    if (draftAvailable?.data) {
      setRecipeData(draftAvailable.data);
    }
    setDraftAvailable(null);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem("astrup_create_recipe_draft");
    setDraftAvailable(null);
  };

  const handleRecipeExtracted = (aiData) => {
    setRecipeData(prev => ({
      ...prev,
      recipe_name: aiData.recipe_name || prev.recipe_name,
      description: aiData.description || prev.description,
      cuisine: aiData.cuisine || prev.cuisine,
      prep_time: aiData.prep_time || prev.prep_time,
      cook_time: aiData.cook_time || prev.cook_time,
      servings: aiData.servings || prev.servings,
      difficulty: aiData.difficulty || prev.difficulty,
      ingredients: aiData.ingredients && aiData.ingredients.length > 0 ? aiData.ingredients : prev.ingredients,
      steps: aiData.steps && aiData.steps.length > 0 ? aiData.steps : prev.steps,
      tags: aiData.tags && aiData.tags.length > 0 ? aiData.tags : prev.tags
    }));
    setError(null);
  };

  // --- 2. DUPLICATE INGREDIENTS DETECTION ---
  const duplicateIngredients = useMemo(() => {
    return findDuplicateIngredients(recipeData.ingredients);
  }, [recipeData.ingredients]);

  // --- 3. INPUT HANDLERS ---
  const handleChange = (e) => {
    setRecipeData({ ...recipeData, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name]) {
      setValidationErrors({ ...validationErrors, [e.target.name]: null });
    }
  };

  const handleCuisineChange = (event, newValue) => {
    const clean = newValue ? toTitleCase(newValue) : "";
    setRecipeData({ ...recipeData, cuisine: clean });
    if (validationErrors.cuisine) {
      setValidationErrors({ ...validationErrors, cuisine: null });
    }
  };

  const handleTagsChange = (event, newValue) => {
    setRecipeData({ ...recipeData, tags: newValue });
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...recipeData.ingredients];
    updated[index][field] = value;
    setRecipeData({ ...recipeData, ingredients: updated });
  };

  const handleIngredientNameBlur = (index, value) => {
    const cleaned = toTitleCase(value);
    if (cleaned !== value) {
      handleIngredientChange(index, "ingredient_name", cleaned);
    }
  };

  const handleUnitBlur = (index, value) => {
    const standardized = getStandardizedUnit(value);
    if (standardized && standardized !== value) {
      handleIngredientChange(index, "measurement_name", standardized);
    }
  };

  const handleAmountBlur = (index, value) => {
    const parsed = parseFraction(value);
    if (parsed !== value) {
      handleIngredientChange(index, "amount", String(parsed));
    }
  };

  const addIngredientField = () => {
    setRecipeData({
      ...recipeData,
      ingredients: [...recipeData.ingredients, { ingredient_name: "", amount: "", measurement_name: "" }]
    });
  };

  const deleteIngredient = (index) => {
    const updated = recipeData.ingredients.filter((_, i) => i !== index);
    setRecipeData({
      ...recipeData,
      ingredients: updated.length > 0 ? updated : [{ ingredient_name: "", amount: "", measurement_name: "" }]
    });
  };

  // Quick Paste Handler
  const handleQuickPasteSubmit = () => {
    if (!quickPasteText.trim()) return;
    const parsed = parseBulkIngredients(quickPasteText);
    if (parsed.length === 0) return;

    if (quickPasteMode === "replace") {
      setRecipeData({ ...recipeData, ingredients: parsed });
    } else {
      const existing = recipeData.ingredients.filter(i => i.ingredient_name.trim() !== "");
      setRecipeData({ ...recipeData, ingredients: [...existing, ...parsed] });
    }

    setQuickPasteText("");
    setQuickPasteOpen(false);
  };

  // Steps Handlers
  const handleStepChange = (index, value) => {
    const updatedSteps = [...recipeData.steps];
    updatedSteps[index].instruction = value;
    setRecipeData({ ...recipeData, steps: updatedSteps });
  };

  const addStepField = () => {
    setRecipeData({
      ...recipeData,
      steps: [...recipeData.steps, { id: Date.now(), step_number: recipeData.steps.length + 1, instruction: "" }]
    });
  };

  const deleteStep = (index) => {
    const updatedSteps = recipeData.steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, step_number: i + 1 }));
    setRecipeData({
      ...recipeData,
      steps: updatedSteps.length > 0 ? updatedSteps : [{ id: Date.now(), step_number: 1, instruction: "" }]
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setRecipeData((prevData) => {
        const oldIndex = prevData.steps.findIndex((step) => step.id === active.id);
        const newIndex = prevData.steps.findIndex((step) => step.id === over.id);
        const reordered = arrayMove(prevData.steps, oldIndex, newIndex).map((step, index) => ({
          ...step,
          step_number: index + 1
        }));
        return { ...prevData, steps: reordered };
      });
    }
  };

  // --- 4. PHOTO HANDLING (DIRECT UPLOAD & URL) ---
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to upload photos.");
      return;
    }

    setIsUploadingImage(true);
    setError(null);

    try {
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const storageRef = ref(storage, `recipe_images/${user.uid}/${Date.now()}_${cleanFileName}`);
      const snap = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snap.ref);

      setRecipeData(prev => ({
        ...prev,
        images: [...prev.images, downloadUrl]
      }));
    } catch (err) {
      console.error("Photo upload error:", err);
      setError("Failed to upload image. Please check your connection or image size.");
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAddManualUrl = () => {
    if (!manualImageUrl.trim()) return;
    setRecipeData(prev => ({
      ...prev,
      images: [...prev.images, manualImageUrl.trim()]
    }));
    setManualImageUrl("");
  };

  const handleDeleteImage = (index) => {
    setRecipeData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // --- 5. WEB SCRAPER IMPORT ---
  const handleImport = async () => {
    if (!importUrl) return;
    setIsImporting(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Login required to import.");
        setIsImporting(false);
        return;
      }

      const token = await user.getIdToken();
      const response = await axios.post(SCRAPE_URL, { url: importUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const imported = response.data;

      const cleanIngredients = (imported.ingredients || []).map(ing => {
        const rawText = typeof ing === 'string' ? ing : (ing.ingredient_name || ing.original_text || "");
        if (!ing.amount || ing.amount === "") {
          return parseIngredientLine(rawText);
        }
        return {
          ingredient_name: toTitleCase(rawText),
          amount: String(parseFraction(ing.amount)),
          measurement_name: getStandardizedUnit(ing.measurement_name || ing.measurement_unit || "")
        };
      });

      const cleanSteps = (imported.steps || []).map((s, i) => {
        const text = typeof s === 'string' ? s : s.instruction;
        return { id: Date.now() + i, step_number: i + 1, instruction: text };
      });

      setRecipeData({
        ...recipeData,
        recipe_name: imported.title ? toTitleCase(imported.title) : "",
        description: imported.description || `Imported from ${importUrl}`,
        cuisine: imported.cuisine ? toTitleCase(imported.cuisine) : "General",
        prep_time: imported.prep_time || 0,
        cook_time: imported.cook_time || 0,
        servings: String(imported.servings || "4"),
        steps: cleanSteps.length > 0 ? cleanSteps : [{ id: Date.now(), step_number: 1, instruction: "" }],
        ingredients: cleanIngredients.length > 0 ? cleanIngredients : [{ ingredient_name: "", amount: "", measurement_name: "" }],
        images: imported.image ? [imported.image] : []
      });
    } catch (err) {
      console.error("Import error:", err);
      setError("Could not import recipe. Check the URL or try entering it manually.");
    } finally {
      setIsImporting(false);
    }
  };

  // --- 6. SUBMIT TO FIRESTORE & SYNC DICTIONARY ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    const errors = {};
    if (!recipeData.recipe_name.trim()) errors.recipe_name = "Recipe name is required";
    if (!recipeData.cuisine.trim()) errors.cuisine = "Cuisine is required";
    if (!recipeData.servings) errors.servings = "Required";

    const validIngredients = recipeData.ingredients.filter(i => i.ingredient_name.trim() !== "");
    if (validIngredients.length === 0) {
      errors.ingredients = "At least one ingredient is required";
    }

    const validSteps = recipeData.steps.filter(s => s.instruction.trim() !== "");
    if (validSteps.length === 0) {
      errors.steps = "At least one instruction step is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fill out the required recipe fields.");
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);
    const user = auth.currentUser;

    if (!user) {
      setError("You must be logged in to create a recipe.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Get author display name
      const userDoc = await getDoc(doc(db, "users", user.uid));
      let authorName = "Chef";
      if (userDoc.exists()) {
        const uData = userDoc.data();
        if (uData.username && !uData.username.includes("@")) {
          authorName = uData.username;
        } else if (uData.first_name || uData.firstName) {
          authorName = `${uData.first_name || uData.firstName} ${uData.last_name || uData.lastName || ""}`.trim();
        } else if (uData.username) {
          authorName = uData.username.split("@")[0];
        } else if (user.displayName && !user.displayName.includes("@")) {
          authorName = user.displayName;
        } else {
          authorName = user.email ? user.email.split("@")[0] : "Chef";
        }
      } else {
        authorName = (user.displayName && !user.displayName.includes("@"))
          ? user.displayName
          : (user.email ? user.email.split("@")[0] : "Chef");
      }

      // Standardize clean ingredients
      const standardizedIngredients = validIngredients.map(ing => ({
        ingredient_name: toTitleCase(ing.ingredient_name),
        amount: ing.amount ? String(parseFraction(ing.amount)) : "",
        measurement_name: getStandardizedUnit(ing.measurement_name)
      }));

      // Standardize clean steps
      const standardizedSteps = validSteps.map((step, idx) => ({
        step_number: idx + 1,
        instruction: step.instruction.trim()
      }));

      const finalCuisine = toTitleCase(recipeData.cuisine);

      const submissionData = {
        recipe_name: toTitleCase(recipeData.recipe_name),
        description: recipeData.description.trim() || "Delicious family recipe.",
        cuisine: finalCuisine,
        difficulty: recipeData.difficulty || "Medium",
        prep_time: Number(recipeData.prep_time) || 0,
        cook_time: Number(recipeData.cook_time) || 0,
        servings: Number(recipeData.servings) || 4,
        ingredients: standardizedIngredients,
        steps: standardizedSteps,
        images: recipeData.images.filter(img => img && img.trim() !== ""),
        tags: (recipeData.tags || []).map(t => toTitleCase(t)),
        user_id: user.uid,
        username: authorName,
        author_email: user.email || "",
        created_at: serverTimestamp(),
        rating: 0,
        rating_count: 0,
        raters: {},
        is_featured: false
      };

      // Save to recipes collection
      await addDoc(collection(db, "recipes"), submissionData);

      // Successfully saved - clear unsaved draft
      try {
        localStorage.removeItem("astrup_create_recipe_draft");
      } catch (e) {}

      // Asynchronously update central dictionary with any new ingredients or cuisines
      try {
        const dictRef = doc(db, "metadata", "dictionary");
        const dictSnap = await getDoc(dictRef);
        if (dictSnap.exists()) {
          const dictData = dictSnap.data();
          const currentCuisines = new Set(dictData.cuisines || []);
          const currentIngredients = new Set(dictData.ingredients || []);
          let hasUpdates = false;

          if (finalCuisine && !currentCuisines.has(finalCuisine)) {
            currentCuisines.add(finalCuisine);
            hasUpdates = true;
          }

          standardizedIngredients.forEach(i => {
            if (i.ingredient_name && !currentIngredients.has(i.ingredient_name)) {
              currentIngredients.add(i.ingredient_name);
              hasUpdates = true;
            }
          });

          if (hasUpdates) {
            await updateDoc(dictRef, {
              cuisines: Array.from(currentCuisines).sort(),
              ingredients: Array.from(currentIngredients).sort()
            });
          }
        }
      } catch (dictErr) {
        console.log("Dictionary update note:", dictErr);
      }

      navigate("/recipes");
    } catch (err) {
      console.error("Submission failed:", err);
      setError("Failed to create recipe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mb: 8, mt: 3 }}>
      <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
          Create a New Recipe
        </Typography>

        {/* DRAFT RECOVERY BANNER */}
        {draftAvailable && (
          <Alert
            severity="info"
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button color="primary" size="small" variant="contained" onClick={handleRestoreDraft}>
                  Restore Draft
                </Button>
                <Button color="inherit" size="small" onClick={handleDiscardDraft}>
                  Discard
                </Button>
              </Box>
            }
          >
            <strong>Unsaved Draft Found:</strong> You have an unsaved recipe draft from {draftAvailable.savedAt}.
          </Alert>
        )}

        {/* 1. SMART CREATION SHORTCUTS: AI ASSISTANT & AUTO-IMPORT */}
        <Grid container spacing={2} sx={{ mb: 3.5 }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                height: '100%',
                bgcolor: '#FFFDF9',
                borderColor: '#FFD54F',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#D28415' }}>
                  <AutoAwesomeIcon fontSize="small" /> AI Recipe Assistant
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                  Scan a handwritten card photo or generate from rough notes with Gemini AI.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setAiDialogOpen(true)}
                sx={{
                  bgcolor: '#D28415',
                  '&:hover': { bgcolor: '#b36b10' },
                  fontWeight: 'bold',
                  textTransform: 'none'
                }}
              >
                Scan Card or Prompt AI
              </Button>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#f9fbfd', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudDownloadIcon color="primary" fontSize="small" /> Auto-Import Recipe from Web
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  Paste any recipe website link (AllRecipes, FoodNetwork, etc.)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Paste URL..."
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={isImporting || !importUrl.trim()}
                  sx={{ minWidth: 90 }}
                >
                  {isImporting ? <CircularProgress size={20} color="inherit" /> : "Import"}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          
          {/* 2. BASIC DETAILS */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Basic Information
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="recipe_name"
                label="Recipe Title"
                value={recipeData.recipe_name}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.recipe_name}
                helperText={validationErrors.recipe_name}
                placeholder="e.g. Grandma's Famous Lasagna"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={cuisinesList}
                value={recipeData.cuisine}
                onChange={handleCuisineChange}
                onInputChange={(event, newInputValue) => {
                  setRecipeData(prev => ({ ...prev, cuisine: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cuisine Type"
                    required
                    error={!!validationErrors.cuisine}
                    helperText={validationErrors.cuisine || "Select or type a custom cuisine"}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  name="difficulty"
                  value={recipeData.difficulty}
                  label="Difficulty"
                  onChange={handleChange}
                >
                  <MenuItem value="Easy">Easy (Beginner friendly)</MenuItem>
                  <MenuItem value="Medium">Medium (Average home cooking)</MenuItem>
                  <MenuItem value="Hard">Hard (Advanced techniques)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="description"
                label="Story & Description"
                value={recipeData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Where did this recipe come from? Any special tips or memories?"
              />
            </Grid>
          </Grid>

          {/* 3. TIME & SERVINGS */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333', mt: 2 }}>
            Time & Servings
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="prep_time"
                label="Prep Time (minutes)"
                value={recipeData.prep_time}
                onChange={handleChange}
                fullWidth
                placeholder="e.g. 15"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="cook_time"
                label="Cook Time (minutes)"
                value={recipeData.cook_time}
                onChange={handleChange}
                fullWidth
                placeholder="e.g. 30"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                name="servings"
                label="Servings"
                value={recipeData.servings}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.servings}
                placeholder="e.g. 4"
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>

          {/* 4. TAGS */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333', mt: 2 }}>
            Recipe Tags
          </Typography>
          <Box sx={{ mb: 3.5 }}>
            <Autocomplete
              multiple
              freeSolo
              options={existingTags}
              value={recipeData.tags}
              onChange={handleTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Tags"
                  placeholder="e.g. Dinner, Quick, Holiday, Comfort Food"
                  helperText="Press Enter to add custom tags"
                />
              )}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 5. INGREDIENTS SECTION */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>
                Ingredients
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Auto-complete suggests standard pantry items as you type.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<ContentPasteIcon />}
              onClick={() => setQuickPasteOpen(true)}
              sx={{ fontWeight: 'bold' }}
            >
              Quick-Paste List
            </Button>
          </Box>

          {/* Duplicate warning alert */}
          {duplicateIngredients.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Possible Duplicate:</strong> You have added <em>{duplicateIngredients.join(", ")}</em> more than once. You may want to combine amounts.
            </Alert>
          )}

          {validationErrors.ingredients && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.ingredients}</Alert>
          )}

          {recipeData.ingredients.map((ing, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              {/* Amount */}
              <TextField
                label="Amount"
                value={ing.amount}
                onChange={(e) => handleIngredientChange(index, "amount", e.target.value)}
                onBlur={(e) => handleAmountBlur(index, e.target.value)}
                placeholder="1 1/2"
                size="small"
                sx={{ width: { xs: '25%', sm: '18%' } }}
              />

              {/* Unit Autocomplete */}
              <Autocomplete
                freeSolo
                options={STANDARD_MEASUREMENTS}
                value={ing.measurement_name}
                onChange={(event, newValue) => {
                  handleIngredientChange(index, "measurement_name", newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  handleIngredientChange(index, "measurement_name", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Unit"
                    size="small"
                    placeholder="cup"
                    onBlur={(e) => handleUnitBlur(index, e.target.value)}
                  />
                )}
                sx={{ width: { xs: '30%', sm: '22%' } }}
              />

              {/* Ingredient Name Autocomplete */}
              <Autocomplete
                freeSolo
                options={ingredientsList}
                value={ing.ingredient_name}
                onChange={(event, newValue) => {
                  handleIngredientChange(index, "ingredient_name", newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  handleIngredientChange(index, "ingredient_name", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ingredient Name"
                    size="small"
                    required
                    fullWidth
                    placeholder="e.g. Olive Oil"
                    onBlur={(e) => handleIngredientNameBlur(index, e.target.value)}
                  />
                )}
                sx={{ flexGrow: 1 }}
              />

              <IconButton onClick={() => deleteIngredient(index)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Button startIcon={<AddIcon />} onClick={addIngredientField} variant="text" sx={{ mt: 0.5, mb: 3 }}>
            Add Another Ingredient
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* 6. INSTRUCTIONS SECTION */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Instructions
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Drag and drop using the handle to reorder cooking steps.
          </Typography>

          {validationErrors.steps && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationErrors.steps}</Alert>
          )}

          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={recipeData.steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {recipeData.steps.map((step, index) => (
                <SortableStep
                  key={step.id}
                  step={step}
                  index={index}
                  handleStepChange={handleStepChange}
                  deleteStep={deleteStep}
                  error={validationErrors[`steps[${index}]`]}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Button startIcon={<AddIcon />} onClick={addStepField} variant="text" sx={{ mt: 0.5, mb: 3 }}>
            Add Next Step
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* 7. PHOTOS SECTION */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
            Recipe Photos
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Upload directly from your phone or computer, or paste a photo link.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
            {/* Direct Upload Button */}
            <input
              type="file"
              accept="image/*"
              hidden
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={isUploadingImage ? <CircularProgress size={18} /> : <AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingImage}
              sx={{ fontWeight: 'bold' }}
            >
              {isUploadingImage ? "Uploading..." : "Upload Photo"}
            </Button>

            {/* URL Fallback Input */}
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Or paste image URL"
                value={manualImageUrl}
                onChange={(e) => setManualImageUrl(e.target.value)}
                placeholder="https://..."
              />
              <Button variant="contained" onClick={handleAddManualUrl} disabled={!manualImageUrl.trim()}>
                Add URL
              </Button>
            </Box>
          </Box>

          {/* Photos Preview Gallery */}
          {recipeData.images.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
              {recipeData.images.map((imgUrl, index) => (
                <Box key={index} sx={{ position: 'relative', width: 110, height: 110, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                  <Box
                    component="img"
                    src={imgUrl}
                    alt={`Recipe photo ${index + 1}`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.src = "/no-image.png"; }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(211,47,47,0.9)' },
                      p: 0.5
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* 8. SUBMIT BUTTON */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ minWidth: 220, width: { xs: '100%', sm: 'auto' }, py: 1.5, fontWeight: 'bold', fontSize: '1.05rem', backgroundColor: '#477491' }}
            >
              {isSubmitting ? <CircularProgress size={26} color="inherit" /> : "Publish Recipe"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* QUICK-PASTE INGREDIENTS DIALOG */}
      <Dialog open={quickPasteOpen} onClose={() => setQuickPasteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Quick-Paste Ingredients</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Paste your ingredients list (one per line). The smart parser will automatically extract amounts, units, and standardize ingredient names!
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={8}
            fullWidth
            placeholder={"1 1/2 cups all-purpose flour\n2 tbsp sugar\n1 tsp vanilla extract\n1/2 cup softened butter\n2 eggs\n1/4 tsp salt"}
            value={quickPasteText}
            onChange={(e) => setQuickPasteText(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={quickPasteMode}
              label="Action"
              onChange={(e) => setQuickPasteMode(e.target.value)}
            >
              <MenuItem value="replace">Replace all current ingredients</MenuItem>
              <MenuItem value="append">Add to existing ingredients</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setQuickPasteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleQuickPasteSubmit}
            variant="contained"
            color="primary"
            disabled={!quickPasteText.trim()}
          >
            Parse & Insert Ingredients
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Recipe Assistant Dialog (Gemini Multimodal OCR & Prompt Parser) */}
      <AiRecipeAssistantDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        onRecipeExtracted={handleRecipeExtracted}
      />
    </Container>
  );
};

export default CreateRecipe;